// Grant / investment record detail modal — opened from the cap-table by
// clicking a non-parent row. Reuses the editable ProfileGrantCard so
// admins can tweak the 4 factors + share class right from the cap-table
// and have the change propagate via _patchGrant / _flushGrant to both
// the profile page and the cap table itself.
//
// State: root.grantDetail holds the current grant being edited (or null
// when the modal is closed). It mirrors a row from root.userGrants so
// ProfileGrantCard's `childrenAs: 'state'` wiring can reuse it verbatim.

import { sb } from '../functions/index.js'
import { liveShares as _liveSharesFor } from '../functions/grantMath.js'
import * as grantsWriter from '../functions/grantsWriter.js'

const _numberFmt = new Intl.NumberFormat('en-US')
const _fmt = (n) => (n == null ? '—' : _numberFmt.format(n))

// Flush the current grant edits to Supabase, then reload the row so
// the generated `shares` / `equity_pct` columns + the vested-shares
// trigger output propagate back into `root.userGrants`. The cap-table
// page watches `userGrants` via its `onUpdate` signature and rebuilds
// whenever any tracked field (including shares_manual) changes, so
// merging the refreshed record here auto-refreshes the cap table.
const _flushGrantFromModal = async (root, grantId) => {
  if (!root || !grantId) return
  const g = (root.userGrants || []).find(x => x.id === grantId)
  if (!g) return
  root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'saving' } })
  try {
    const patch = {
      label: g.label,
      share_class: g.share_class,
      vesting_start: g.vesting_start,
      vesting_end: g.vesting_end,
      vesting_months: g.vesting_months,
      origin_valuation: g.origin_valuation,
      salary_monthly: g.salary_monthly,
      hourly_rate: g.hourly_rate,
      weekly_hours: g.weekly_hours,
      investment: g.investment,
      investment_type: g.investment_type,
      discount: g.discount,
      shares_manual: g.shares_manual,
      status: g.status
    }
    const { data } = await grantsWriter.updateGrant(grantId, patch)
    if (data) {
      const merged = (root.userGrants || []).map(ug => ug.id === grantId ? data : ug)
      root.update({ userGrants: merged })
    }
    // DOMQL doesn't call onUpdate on the cap-table page when root state
    // changes elsewhere, so the cap-table page exposes a refetch hook
    // we invoke explicitly. This rebuilds root.capTable from the fresh
    // userGrants, refreshing the rows the user sees behind the modal.
    if (typeof root.__refetchCapTable === 'function') {
      try { root.__refetchCapTable() } catch (e) { console.warn('[cap refetch]', e) }
    }
    root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'saved' } })
    setTimeout(() => {
      root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: null } })
    }, 2000)
  } catch (e) {
    console.warn('[grant flush]', e)
    root.update({ _grantSaveStatus: { ...(root._grantSaveStatus || {}), [grantId]: 'error' } })
  }
}

// Resolve the grant being shown. Pull the fresh record from
// root.userGrants so edits from elsewhere flow through instead of
// freezing a snapshot at open time.
const _g = (s) => {
  const open = s.root?.grantDetail
  if (!open?.id) return null
  return (s.root?.userGrants || []).find(ug => ug.id === open.id) || open
}

// Owner person record — for name in the header subtitle.
const _owner = (s) => {
  const g = _g(s)
  if (!g?.user_id) return {}
  return (s.root?.people || []).find(p => p.userId === g.user_id) || {}
}

export const CapTableGrantModal = {
  extends: 'Modal',
  stateKey: 'grantDetail',

  Window: {
    extends: 'ModalWindow',
    width: '100%',
    maxWidth: 'I1',    // ~840px — wide enough for factors side-by-side without overflowing the viewport
    maxHeight: '90dvh',
    padding: 'C',
    gap: 'B',
    overflow: 'auto',

    // Use the platform ModalHeader slot so Close + action icons align with
    // every other modal (ConfirmModal, EventDetailModal, etc.).
    ModalHeader: {
      Hgroup: {
        H: { text: (el, s) => _owner(s).name || 'Grant' },
        P: {
          text: null,
          flow: 'x',
          align: 'center left',
          gap: 'Z1',
          Eyebrow: {
            tag: 'span',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: (el, s) => (_g(s)?.investment != null) ? 'purple' : 'green',
            text: (el, s) => (_g(s)?.investment != null) ? 'Investment' : 'Equity grant'
          },
          Dot: { tag: 'span', color: 'caption', text: '·' },
          Summary: {
            tag: 'span',
            color: 'caption',
            text: (el, s) => {
              const g = _g(s)
              if (!g) return ''
              if (g.investment != null) {
                return `$${_fmt(g.investment)} @ $${_fmt(g.origin_valuation || 0)}`
              }
              return `${_fmt(g.shares || 0)} shares · ${((g.shares || 0) / 10000000 * 100).toFixed(2)}%`
            }
          }
        }
      },
      Icons: {
        // "Expand to profile" — jumps to the owner's full profile page.
        OpenProfile: {
          extends: 'IconButton',
          title: 'Open profile',
          aria: { label: 'Open profile' },
          Icon: { icon: 'link' },
          onClick: (e, el, s) => {
            const owner = _owner(s)
            s.root.update({ grantDetail: null, profileEmail: owner.email || null })
          }
        },
        // Remove (trash) — confirm, delete the grant, close the modal.
        // Ordered just before the base Close button (order: 10) so it
        // sits immediately left of the X in the header.
        DeleteGrant: {
          extends: 'IconButton',
          order: 9,
          title: 'Remove grant',
          aria: { label: 'Remove grant' },
          Icon: { icon: 'trash' },
          onClick: (e, el, s) => {
            const g = _g(s)
            if (!g) return
            const label = g.label || (g.investment != null ? 'this investment' : 'this grant')
            if (!window.confirm(`Remove ${label}?`)) return
            s.root.update({
              userGrants: (s.root?.userGrants || []).filter(ug => ug.id !== g.id),
              grantDetail: null
            })
            // Fire off the DB delete in the background.
            try {
              grantsWriter.deleteGrant(g.id).catch(() => {})
            } catch {}
          }
        }
      }
    },

    // Editable card — reuses ProfileGrantCard which already handles all
    // 4 factors + share class + save status. `childrenAs: 'state'` feeds
    // each child the grant record as its own state (same pattern as the
    // GrantsList inside MemberProfile).
    EditableCardWrap: {
      flow: 'y',
      childExtends: 'ProfileGrantCard',
      childrenAs: 'state',
      // `childProps` factory lifts the grant's id to `key` at the top
      // level of the child definition. With childrenAs='state' the
      // children array items are wrapped into `{ state: item }`, which
      // hides any inline `key` — so we have to route it through
      // childProps to surface it as the stable reconciliation id. This
      // prevents DOMQL from tearing down the card (and its focused
      // inputs) on every keystroke patch.
      childProps: (el, grant) => ({ key: grant?.id || 'none' }),
      children: (el, s) => {
        const g = _g(s)
        return g ? [g] : []
      }
    },

    // Read-only totals below the editable card — value at current
    // valuation and company-wide %.
    TotalsBlock: {
      flow: 'y',
      padding: 'A 0 0 0',
      gap: 'Y',

      RowValueAtCurrent: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        Label: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'Value at current valuation' },
        Val: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'green',
          text: (el, s) => {
            const g = _g(s)
            const v = s.root?.companySettings?.current_valuation
            if (!g || !v) return '—'
            // Use live shares so this tracks shares_manual edits without
            // waiting for a DB save + refetch to refresh `g.shares`.
            const shares = _liveSharesFor(g)
            if (!shares) return '—'
            return `$${_fmt(Math.round((shares / 10000000) * v))}`
          }
        }
      },

      RowEquityPct: {
        flow: 'x',
        align: 'center space-between',
        padding: 'Z 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',
        Label: { tag: 'span', fontSize: 'Z2', color: 'caption', text: '% of company' },
        Val: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'blue',
          text: (el, s) => {
            const g = _g(s)
            if (!g) return '—'
            const shares = _liveSharesFor(g)
            if (!shares) return '—'
            return `${((shares / 10000000) * 100).toFixed(4)}%`
          }
        }
      },

      // Vested readout with a progress bar — label on the left, value +
      // bar grouped on the right for a cleaner label/value rhythm that
      // matches the other totals rows above it.
      RowVested: {
        hide: (el, s) => (_g(s)?.investment != null),
        flow: 'x',
        align: 'center space-between',
        gap: 'A',
        padding: 'Z 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'line',

        Label: { tag: 'span', fontSize: 'Z2', color: 'caption', text: 'Vested' },

        // Right cluster — value text + visual progress bar, right-aligned.
        ValueGroup: {
          flow: 'x',
          align: 'center',
          gap: 'A',
          flex: '1',
          justifyContent: 'flex-end',

          Val: {
            tag: 'span',
            fontSize: 'A',
            fontWeight: '700',
            color: 'green',
            text: (el, s) => {
              const g = _g(s)
              if (!g?.shares) return '—'
              const pct = Math.round(((g.vested_shares || 0) / g.shares) * 100)
              return `${_fmt(g.vested_shares || 0)} · ${pct}%`
            }
          },

          VestedBar: {
            width: 'E',
            maxWidth: 'E',
            height: 'Y',
            round: 'Y',
            background: 'line',
            overflow: 'hidden',
            flexShrink: '0',
            Fill: {
              height: '100%',
              round: 'Y',
              background: 'green',
              width: (el, s) => {
                const g = _g(s)
                if (!g?.shares) return '0%'
                const pct = Math.min(100, Math.round(((g.vested_shares || 0) / g.shares) * 100))
                return `${pct}%`
              }
            }
          }
        }
      }
    },

    // Footer — Save action + status chip. Dirty state lights up the
    // button blue; saving/saved/error feedback appears beside it.
    ModalFooter: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',
      padding: 'A 0 0 0',

      StatusChip: {
        hide: (el, s) => {
          const g = _g(s)
          return !g || !(s.root?._grantSaveStatus || {})[g.id]
        },
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '600',
        padding: 'V Z',
        round: 'V',
        background: (el, s) => {
          const g = _g(s); if (!g) return 'overlay'
          const st = (s.root?._grantSaveStatus || {})[g.id]
          if (st === 'saved') return 'green.12'
          if (st === 'error') return 'red.12'
          if (st === 'dirty') return 'yellow.12'
          return 'blue.12'
        },
        color: (el, s) => {
          const g = _g(s); if (!g) return 'caption'
          const st = (s.root?._grantSaveStatus || {})[g.id]
          if (st === 'saved') return 'green'
          if (st === 'error') return 'red'
          if (st === 'dirty') return 'yellow'
          return 'blue'
        },
        text: (el, s) => {
          const g = _g(s); if (!g) return ''
          const st = (s.root?._grantSaveStatus || {})[g.id]
          if (st === 'saved') return '✓ Saved'
          if (st === 'error') return 'Save failed'
          if (st === 'dirty') return '● Unsaved'
          return 'Saving…'
        }
      },

      SaveBtn: {
        marginLeft: 'auto',
        tag: 'button',
        type: 'button',
        padding: 'Z A1',
        round: 'Z',
        border: 'none',
        background: (el, s) => {
          const g = _g(s); if (!g) return 'overlayStrong'
          return ((s.root?._grantSaveStatus || {})[g.id] === 'dirty') ? 'blue' : 'overlayStrong'
        },
        color: (el, s) => {
          const g = _g(s); if (!g) return 'subtitle'
          return ((s.root?._grantSaveStatus || {})[g.id] === 'dirty') ? 'white' : 'subtitle'
        },
        fontSize: 'Z2',
        fontWeight: '700',
        cursor: 'pointer',
        text: 'Save',
        ':hover': { opacity: '.9' },
        onClick: (e, el, s) => {
          const g = _g(s)
          if (g) _flushGrantFromModal(s.root, g.id)
        }
      }
    }
  }
}
